import { useState, useEffect } from 'react'
import { message } from 'antd'

const usePainMedication = () => {
  const [loading, setLoading] = useState(false)
  const [medications, setMedications] = useState([])
  const [painManagementOptions, setPainManagementOptions] = useState([])

  useEffect(() => {
    const fetchPainManagementData = async () => {
      setLoading(true)
      try {
        // Simulate API call to fetch pain management data
        await new Promise((resolve) => setTimeout(resolve, 800))

        // Normally we would fetch from an API
        // For now, we'll use the data from the local mock data
        let medicationsData = []
        let optionsData = []

        try {
          const module = await import('../utils/painManagement.json')
          if (module && module.default) {
            medicationsData = module.default.medications || []
            optionsData = module.default.options || []
          } else {
            console.error('Error loading painManagement.json: Module or default export not found')
            message.error('Failed to load pain management data')
          }
        } catch (importError) {
          console.error('Error importing painManagement.json:', importError)
          message.error('Failed to load pain management data')
        }

        setMedications(medicationsData)
        setPainManagementOptions(optionsData)
      } catch (error) {
        console.error('Error fetching pain management data:', error)
        message.error('Failed to load pain management data')
      } finally {
        setLoading(false)
      }
    }

    fetchPainManagementData()
  }, [])

  const addMedication = async (medicationData) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const newMedication = {
        id: `med-${Date.now()}`,
        ...medicationData
      }

      setMedications([...medications, newMedication])
      return newMedication
    } catch (error) {
      console.error('Error adding medication:', error)
      message.error('Failed to add medication')
      return null
    } finally {
      setLoading(false)
    }
  }

  const updateMedication = async (medicationId, medicationData) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const updatedMedications = medications.map((medication) => {
        if (medication.id === medicationId) {
          return { ...medication, ...medicationData }
        }
        return medication
      })

      setMedications(updatedMedications)
      return true
    } catch (error) {
      console.error('Error updating medication:', error)
      message.error('Failed to update medication')
      return false
    } finally {
      setLoading(false)
    }
  }

  const deleteMedication = async (medicationId) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))

      const updatedMedications = medications.filter((medication) => medication.id !== medicationId)
      setMedications(updatedMedications)
      return true
    } catch (error) {
      console.error('Error deleting medication:', error)
      message.error('Failed to delete medication')
      return false
    } finally {
      setLoading(false)
    }
  }

  const updateAdherence = async (medicationId, adherenceValue) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 400))

      const updatedMedications = medications.map((medication) => {
        if (medication.id === medicationId) {
          return { ...medication, adherence: adherenceValue }
        }
        return medication
      })

      setMedications(updatedMedications)
      return true
    } catch (error) {
      console.error('Error updating medication adherence:', error)
      message.error('Failed to update medication adherence')
      return false
    } finally {
      setLoading(false)
    }
  }

  const recordPainLevel = async (patientId, painLevel, notes) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))

      // In a real app, we would send this to the backend

      return {
        id: `pain-record-${Date.now()}`,
        patientId,
        painLevel,
        notes,
        timestamp: new Date().toISOString()
      }
    } catch (error) {
      console.error('Error recording pain level:', error)
      message.error('Failed to record pain level')
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    medications,
    painManagementOptions,
    addMedication,
    updateMedication,
    deleteMedication,
    updateAdherence,
    recordPainLevel
  }
}

export default usePainMedication
